import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { BANCO_IN_STANDBY, bancoConcesso, eOperatoreBancoLocale } from "@/lib/bancoAccesso";

/**
 * Chi sono io rispetto al banco Datacom, in un posto solo.
 *
 * - `operatore`: rispondo io. Lo dice il DATABASE (e_operatore_banco: admin o
 *   `banco_operatore` acceso da un admin, dal 06/10/2026 niente piu' email
 *   interna): cosi' il menu segue la regola vera appena cambia, senza
 *   aspettare un rilascio dell'app. Finche' il database non ha risposto vale
 *   la stessa regola letta dal profilo.
 * - `cliente`: vedo la chat col banco (concessione di Datacom con account
 *   attivo, oppure una richiesta di tecnico aperta: l'ultimo salvagente).
 * - `richiesta`: la richiesta aperta negli ultimi 14 giorni, se c'e'.
 *
 * Prima della migrazione del 24/09 la tabella `richieste_operatore` non
 * esiste: la query risponde «nessuna richiesta» invece di rompere il menu'.
 */
export interface RichiestaOperatore {
  id: string;
  stato: "in_chat" | "prenotata" | "presa" | "chiusa";
  motivo: string;
  per_il: string | null;
  creata_il: string;
}

function tabellaAssente(e: { code?: string; message?: string } | null): boolean {
  if (!e) return false;
  const m = `${e.code ?? ""} ${e.message ?? ""}`;
  return m.includes("42P01") || /richieste_operatore|does not exist|schema cache/i.test(m);
}

export function useBanco() {
  const { user, profile, isAdmin } = useAuth();
  const { data: operatoreDb } = useQuery({
    queryKey: ["e-operatore-banco", user?.id],
    enabled: !!user?.id && !BANCO_IN_STANDBY,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<boolean | null> => {
      const { data, error } = await supabase.rpc("e_operatore_banco");
      if (error) return null;
      return data === true;
    },
  });
  const operatore = BANCO_IN_STANDBY
    ? false
    : typeof operatoreDb === "boolean"
      ? operatoreDb
      : eOperatoreBancoLocale(profile, isAdmin);

  const { data: richiesta = null } = useQuery({
    queryKey: ["richiesta-operatore", user?.id],
    enabled: !!user?.id && !BANCO_IN_STANDBY,
    refetchInterval: 60_000,
    queryFn: async (): Promise<RichiestaOperatore | null> => {
      const da = new Date(Date.now() - 14 * 86_400_000).toISOString();
      const { data, error } = await supabase
        .from("richieste_operatore")
        .select("id, stato, motivo, per_il, creata_il")
        .eq("user_id", user!.id)
        .in("stato", ["in_chat", "prenotata", "presa"])
        .gte("creata_il", da)
        .order("creata_il", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) {
        if (tabellaAssente(error)) return null;
        throw error;
      }
      return (data as unknown as RichiestaOperatore | null) ?? null;
    },
  });

  const cliente = bancoConcesso(profile, isAdmin, !!richiesta);
  return { operatore, cliente, richiesta };
}
